import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import { Button, Collapse, Stack } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { getEmailInboxInfo } from '@/utils/email-inbox-url';
import { authApi } from './auth-api';
import { mapAuthError } from './auth-error-map';
import { getAuthErrorCallbackUrl } from './auth-utils';

// Cooldown (in seconds) after sending before the button becomes clickable again
const COOLDOWN_SECS = 60;

// ─── Main component ───────────────────────────────────────────────────────────

export default function MagicLinkButton({
    email,
    redirectPath,
    oauthQuery,
    variant,
    disabled,
}: {
    email: string;
    redirectPath: string;
    oauthQuery?: string;
    variant: 'contained' | 'outlined';
    disabled?: boolean;
}) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(false);
    const [showInbox, setShowInbox] = useState(false);

    // Cooldown countdown (seconds remaining).
    // null  → never sent yet
    // >0    → cooling down (button disabled, shows countdown)
    // 0     → ready to resend
    const [cooldown, setCooldown] = useState<number | null>(null);
    const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const handleClick = async () => {
        if (loading || (cooldown !== null && cooldown > 0) || !email.trim()) return;
        setShowInbox(false);
        setCooldown(null);
        if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
        }
        setLoading(true);
        try {
            await authApi.signInMagicLink({
                email: email.trim(),
                callbackURL: redirectPath,
                errorCallbackURL: getAuthErrorCallbackUrl(),
                oauthQuery,
            });
            // Explicit confirmation that the email was sent (the cooldown button alone read as
            // "nothing happened" to testers), plus the inbox shortcut and resend cooldown.
            enqueueSnackbar(t('auth.magic-link.sent'), { variant: 'success' });
            setShowInbox(true);
            setCooldown(COOLDOWN_SECS);
            const id = setInterval(() => {
                setCooldown((prev) => {
                    if (prev === null || prev <= 1) {
                        clearInterval(id);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            countdownRef.current = id;
        } catch (error) {
            const mapped = mapAuthError(error, t, 'magic-link');
            enqueueSnackbar(mapped.message, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Clean up interval on unmount
    useEffect(() => {
        return () => {
            if (countdownRef.current) clearInterval(countdownRef.current);
        };
    }, []);

    const inboxInfo = getEmailInboxInfo(email);

    const label = loading
        ? t('auth.common.loading')
        : cooldown === null
          ? t('auth.login.magic-link-option')
          : cooldown === 0
            ? t('str.action.progress.resend-email')
            : t('auth.magic-link.resend-cooldown', { seconds: cooldown });

    return (
        <Stack>
            <Button
                variant={variant}
                size="large"
                fullWidth
                onClick={handleClick}
                disabled={loading || (cooldown !== null && cooldown > 0) || disabled}
            >
                {label}
            </Button>

            {/* Inbox shortcut — visible after a successful send, for known providers */}
            {inboxInfo && (
                <Collapse in={showInbox} timeout={280} sx={{ marginTop: showInbox ? 2 : 0 }}>
                    <Button
                        component="a"
                        href={inboxInfo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth
                        endIcon={<OpenInNewRounded sx={{ fontSize: '0.85rem !important' }} />}
                    >
                        {t('auth.magic-link.open-inbox', { provider: t(inboxInfo.i18nKey) })}
                    </Button>
                </Collapse>
            )}
        </Stack>
    );
}
